import React, { useState } from "react";
import { COMMON_AREA_SYSTEMS } from "../../../../constants/optionalSystems";
import type { CommonAreaSystem } from "../../../../types/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { addCommonAreaSystem } from "../../../../store/features/optionalSystemsSlice";
import { selectors } from "../../../../store/selectors";

const getDefaultSystem = (
  system: (typeof COMMON_AREA_SYSTEMS)[number],
): CommonAreaSystem => ({
  id: `${system.subtype}-${Date.now()}-${Math.random()}`,
  type: "commonArea",
  subtype: system.subtype as CommonAreaSystem["subtype"],
  name: system.name,
  description: system.description,
  cost: system.cost,
  tonnage: system.tonnage,
  power: 0,
  techLevel: system.techLevel,
  properties: { ...system.properties },
});

const CommonAreasSystems: React.FC = () => {
  const dispatch = useAppDispatch();
  // Local state for Advanced Entertainment System card inputs
  const [entertainmentTechLevel, setEntertainmentTechLevel] = useState(5);
  const [entertainmentCost, setEntertainmentCost] = useState(1000);
  // Local state for Brewery/Distillery card input
  const [breweryTonnage, setBreweryTonnage] = useState(0.5);
  // Local state for Gourmet Kitchen card input
  const [gourmetDiners, setGourmetDiners] = useState(1);
  // Local state for Hot Tub card input
  const [hotTubUsers, setHotTubUsers] = useState(1);
  // Local state for Swimming Pool (Standard) card input
  const [swimmingPoolTonnage, setSwimmingPoolTonnage] = useState(4);
  // Local state for Swimming Pool card type (standard or endless)
  const [poolType, setPoolType] = useState<"standard" | "endless">("standard");
  // Local state for Theatre card type (basic or advanced)
  const [theatreType, setTheatreType] = useState<
    "theatreBasic" | "theatreAdvanced"
  >("theatreBasic");
  // Local state for Theatre size
  const [theatreTonnage, setTheatreTonnage] = useState(8);
  // Local state for Zero-G Room size
  const [zeroGRoomTonnage, setZeroGRoomTonnage] = useState(1);

  // Swimming pool system data
  const swimmingPoolStandard = COMMON_AREA_SYSTEMS.find(
    (s) => s.subtype === "swimmingPool",
  );
  const swimmingPoolEndless = COMMON_AREA_SYSTEMS.find(
    (s) => s.subtype === "endlessPool",
  );

  // Theatre system data
  const theatreBasic = COMMON_AREA_SYSTEMS.find(
    (s) => s.subtype === "theatreBasic",
  );
  const theatreAdvanced = COMMON_AREA_SYSTEMS.find(
    (s) => s.subtype === "theatreAdvanced",
  );

  // Use selectors for calculations
  const advancedEntertainmentStats = useAppSelector((state) => {
    const selector = selectors.derived.createAdvancedEntertainmentStatsSelector(
      entertainmentTechLevel,
      entertainmentCost,
    );
    return selector(state);
  });
  const breweryStats = useAppSelector((state) => {
    const selector = selectors.derived.createBreweryStatsSelector(
      breweryTonnage,
      swimmingPoolStandard?.properties?.costPerTon ||
        swimmingPoolStandard?.cost ||
        0,
    );
    return selector(state);
  });
  const gourmetKitchenStats = useAppSelector((state) => {
    const selector = selectors.derived.createGourmetKitchenStatsSelector(
      gourmetDiners,
      swimmingPoolStandard?.properties?.costPerTon ||
        swimmingPoolStandard?.cost ||
        0,
    );
    return selector(state);
  });
  const hotTubStats = useAppSelector((state) => {
    const selector = selectors.derived.createHotTubStatsSelector(
      hotTubUsers,
      0.25,
      swimmingPoolStandard?.properties?.costPerTon ||
        swimmingPoolStandard?.cost ||
        0,
    );
    return selector(state);
  });
  const swimmingPoolStats = useAppSelector((state) => {
    const selector = selectors.derived.createSwimmingPoolStatsSelector(
      poolType,
      swimmingPoolTonnage,
      poolType === "standard"
        ? swimmingPoolStandard?.properties?.costPerTon ||
            swimmingPoolStandard?.cost ||
            0
        : swimmingPoolEndless?.properties?.costPerTon ||
            swimmingPoolEndless?.cost ||
            0,
      poolType === "standard"
        ? swimmingPoolStandard?.techLevel || 0
        : swimmingPoolEndless?.techLevel || 0,
    );
    return selector(state);
  });
  const theatreStats = useAppSelector((state) => {
    const selector = selectors.derived.createTheatreStatsSelector(
      theatreType,
      theatreTonnage,
      theatreType === "theatreBasic"
        ? theatreBasic?.properties?.costPerTon || theatreBasic?.cost || 0
        : theatreAdvanced?.properties?.costPerTon || theatreAdvanced?.cost || 0,
      theatreType === "theatreBasic"
        ? theatreBasic?.techLevel || 0
        : theatreAdvanced?.techLevel || 0,
    );
    return selector(state);
  });
  const zeroGRoomStats = useAppSelector((state) =>
    selectors.derived.selectZeroGRoomStats(state, zeroGRoomTonnage),
  );

  // Handle adding a new system
  const handleAddSystem = (system: (typeof COMMON_AREA_SYSTEMS)[number]) => {
    if (system.subtype === "advancedEntertainment") {
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(system),
          techLevel: advancedEntertainmentStats.techLevel,
          cost: advancedEntertainmentStats.cost,
        }),
      );
    } else if (system.subtype === "brewery") {
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(system),
          tonnage: breweryStats.tonnage,
          cost: breweryStats.cost,
        }),
      );
    } else if (system.subtype === "gourmetKitchen") {
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(system),
          tonnage: gourmetKitchenStats.tonnage,
          cost: gourmetKitchenStats.cost,
          properties: {
            ...system.properties,
            diners: gourmetKitchenStats.diners,
          },
        }),
      );
    } else if (system.subtype === "hotTub") {
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(system),
          tonnage: hotTubStats.tonnage,
          cost: hotTubStats.cost,
          properties: {
            ...system.properties,
            travellers: hotTubStats.travellers,
          },
        }),
      );
    } else if (
      system.subtype === "swimmingPool" ||
      system.subtype === "endlessPool"
    ) {
      // Use poolType and correct data
      const selected =
        poolType === "standard" ? swimmingPoolStandard : swimmingPoolEndless;
      if (!selected) return;
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(selected),
          subtype: poolType === "standard" ? "swimmingPool" : "endlessPool",
          tonnage: swimmingPoolStats.tonnage,
          cost: swimmingPoolStats.cost,
          techLevel: swimmingPoolStats.techLevel,
          properties: {
            ...selected.properties,
            poolType: swimmingPoolStats.poolType,
          },
        }),
      );
    } else if (
      system.subtype === "theatreBasic" ||
      system.subtype === "theatreAdvanced"
    ) {
      const selected =
        theatreType === "theatreBasic" ? theatreBasic : theatreAdvanced;
      if (!selected) return;
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(selected),
          subtype: theatreType,
          tonnage: theatreStats.tonnage,
          cost: theatreStats.cost,
          techLevel: theatreStats.techLevel,
        }),
      );
    } else if (system.subtype === "zeroGRoom") {
      dispatch(
        addCommonAreaSystem({
          ...getDefaultSystem(system),
          tonnage: zeroGRoomStats.tonnage,
          cost: zeroGRoomStats.cost,
        }),
      );
    } else {
      dispatch(addCommonAreaSystem(getDefaultSystem(system)));
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Common Areas</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Available Common Areas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Swimming Pool Combined Card */}
          {swimmingPoolStandard && swimmingPoolEndless && (
            <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Swimming Pool
                </h4>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <label className="flex items-center gap-1 mb-1">
                    Type:
                    <select
                      value={poolType}
                      onChange={(e) =>
                        setPoolType(e.target.value as "standard" | "endless")
                      }
                      className="px-1 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-600 dark:text-gray-100"
                    >
                      <option value="standard">Standard</option>
                      <option value="endless">Endless</option>
                    </select>
                  </label>
                  {poolType === "standard" && (
                    <label className="flex items-center gap-1 mb-1">
                      Size (tons):
                      <input
                        type="number"
                        min={4}
                        step={1}
                        value={swimmingPoolTonnage}
                        onChange={(e) =>
                          setSwimmingPoolTonnage(
                            Math.max(4, Math.floor(Number(e.target.value))),
                          )
                        }
                        className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs"
                      />
                    </label>
                  )}
                  <div>
                    <b>Tech Level:</b> {swimmingPoolStats.techLevel}
                  </div>
                  <div>
                    <b>Base Tonnage:</b>{" "}
                    {poolType === "standard"
                      ? swimmingPoolStandard.tonnage
                      : swimmingPoolEndless.tonnage}
                  </div>
                  <div>
                    <b>Cost per ton:</b>{" "}
                    {formatCurrency(
                      poolType === "standard"
                        ? swimmingPoolStandard.properties.costPerTon ||
                            swimmingPoolStandard.cost
                        : swimmingPoolEndless.properties.costPerTon ||
                            swimmingPoolEndless.cost,
                    )}
                  </div>
                </div>
              </div>
              <button
                className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() =>
                  handleAddSystem(
                    poolType === "standard"
                      ? swimmingPoolStandard
                      : swimmingPoolEndless,
                  )
                }
              >
                Add
              </button>
            </div>
          )}
          {/* Theatre Combined Card */}
          {theatreBasic && theatreAdvanced && (
            <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Theatre</h4>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <label className="flex items-center gap-1 mb-1">
                    Type:
                    <select
                      value={theatreType}
                      onChange={(e) => {
                        const val = e.target.value as
                          | "theatreBasic"
                          | "theatreAdvanced";
                        setTheatreType(val);
                        setTheatreTonnage(val === "theatreBasic" ? 8 : 12);
                      }}
                      className="px-1 py-0.5 border border-gray-300 rounded text-xs"
                    >
                      <option value="theatreBasic">Basic</option>
                      <option value="theatreAdvanced">Advanced</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-1 mb-1">
                    Size (tons):
                    <input
                      type="number"
                      min={theatreType === "theatreBasic" ? 8 : 12}
                      step={1}
                      value={theatreTonnage}
                      onChange={(e) =>
                        setTheatreTonnage(
                          Math.max(
                            theatreType === "theatreBasic" ? 8 : 12,
                            Math.floor(Number(e.target.value)),
                          ),
                        )
                      }
                      className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs"
                    />
                  </label>
                  <div>
                    <b>Tech Level:</b> {theatreStats.techLevel}
                  </div>
                  <div>
                    <b>Base Tonnage:</b>{" "}
                    {theatreType === "theatreBasic"
                      ? theatreBasic.tonnage
                      : theatreAdvanced.tonnage}
                  </div>
                  <div>
                    <b>Cost per ton:</b>{" "}
                    {formatCurrency(
                      theatreType === "theatreBasic"
                        ? theatreBasic.properties.costPerTon ||
                            theatreBasic.cost
                        : theatreAdvanced.properties.costPerTon ||
                            theatreAdvanced.cost,
                    )}
                  </div>
                </div>
              </div>
              <button
                className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() =>
                  handleAddSystem(
                    theatreType === "theatreBasic"
                      ? theatreBasic
                      : theatreAdvanced,
                  )
                }
              >
                Add
              </button>
            </div>
          )}
          {/* Render all other cards except swimmingPool, endlessPool, theatreBasic, theatreAdvanced */}
          {COMMON_AREA_SYSTEMS.filter(
            (system) =>
              system.subtype !== "swimmingPool" &&
              system.subtype !== "endlessPool" &&
              system.subtype !== "theatreBasic" &&
              system.subtype !== "theatreAdvanced",
          ).map((system) => (
            <div
              key={system.subtype}
              className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
            >
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {system.name}
                </h4>
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  {system.subtype === "advancedEntertainment" ? (
                    <>
                      <label className="flex items-center gap-1 mb-1">
                        Tech Level:
                        <input
                          type="number"
                          min={5}
                          max={20}
                          value={entertainmentTechLevel}
                          onChange={(e) =>
                            setEntertainmentTechLevel(Number(e.target.value))
                          }
                          className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                      </label>
                      <label className="flex items-center gap-1 mb-1">
                        Cost:
                        <input
                          type="number"
                          min={100}
                          value={entertainmentCost}
                          onChange={(e) =>
                            setEntertainmentCost(Number(e.target.value))
                          }
                          className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                      </label>
                    </>
                  ) : system.subtype === "brewery" ? (
                    <>
                      <label className="flex items-center gap-1 mb-1">
                        Size (tons):
                        <input
                          type="number"
                          min={0.5}
                          step={0.5}
                          value={breweryTonnage}
                          onChange={(e) =>
                            setBreweryTonnage(
                              Math.max(
                                0.5,
                                Math.round(Number(e.target.value) * 2) / 2,
                              ),
                            )
                          }
                          className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                      </label>
                      <div>
                        <b>Cost per ton:</b>{" "}
                        {formatCurrency(
                          system.properties.costPerTon || system.cost,
                        )}
                      </div>
                    </>
                  ) : system.subtype === "gourmetKitchen" ? (
                    <>
                      <label className="flex items-center gap-1 mb-1">
                        Number of Diners:
                        <input
                          type="number"
                          min={1}
                          value={gourmetDiners}
                          onChange={(e) =>
                            setGourmetDiners(
                              Math.max(1, Number(e.target.value)),
                            )
                          }
                          className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                      </label>
                      <div>
                        <b>Cost per diner:</b>{" "}
                        {formatCurrency(
                          system.properties.costPerTon || system.cost,
                        )}
                      </div>
                    </>
                  ) : system.subtype === "hotTub" ? (
                    <>
                      <label className="flex items-center gap-1 mb-1">
                        Number of Users:
                        <input
                          type="number"
                          min={1}
                          value={hotTubUsers}
                          onChange={(e) =>
                            setHotTubUsers(Math.max(1, Number(e.target.value)))
                          }
                          className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                        />
                      </label>
                      <div>
                        <b>Cost per user:</b>{" "}
                        {formatCurrency(
                          (system.properties.tonsPerTraveller || 0.25) *
                            (system.properties.costPerTon || system.cost),
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <b>Tech Level:</b> {system.techLevel}
                      </div>
                      {system.tonnage > 0 && (
                        <div>
                          <b>Base Tonnage:</b> {system.tonnage}
                        </div>
                      )}
                      <div>
                        <b>Base Cost:</b> {formatCurrency(system.cost)}
                      </div>
                    </>
                  )}
                  {system.subtype === "zeroGRoom" && (
                    <label className="flex items-center gap-1 mb-1">
                      Size (tons):
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={zeroGRoomTonnage}
                        onChange={(e) =>
                          setZeroGRoomTonnage(
                            Math.max(1, Math.floor(Number(e.target.value))),
                          )
                        }
                        className="w-20 px-1 py-0.5 border border-gray-300 rounded text-xs"
                      />
                    </label>
                  )}
                </div>
              </div>
              <button
                className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={() => handleAddSystem(system)}
              >
                Add
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommonAreasSystems;
